'use client';

import React, { createContext, useContext, useMemo, useRef } from 'react';
import { useAuth } from './auth';
import { logManager } from './utils';
import type { PermissionSet, PermissionData, RoleInfo, PermissionSummary } from '@/lib/generated-api/models';

interface PermissionContextType {
  currentTenantPermissions: PermissionData | null;
  crossTenantPermissions: Record<string, PermissionData>;
  effectivePermissions: Record<string, PermissionSet>;
  permissionSummary: PermissionSummary | null;
  hasPermission: (resource: string, action: string, tenantId?: string) => boolean;
  hasAnyPermission: (resource: string, actions: string[], tenantId?: string) => boolean;
  hasAllPermissions: (resource: string, actions: string[], tenantId?: string) => boolean;
  getTenantPermissions: (tenantId: string) => PermissionData | null;
  getAccessibleTenants: () => string[];
  isLoading: boolean;
}

const PermissionContext = createContext<PermissionContextType | null>(null);

export const PermissionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { session, currentTenant, isLoading } = useAuth();
  
  // Use refs to track if we've already logged errors for this session/tenant combination
  const errorLoggedRef = useRef<string>('');

  // Debug: Log what permission data is available
  React.useEffect(() => {
    if (session && currentTenant) {
      console.log(' [PERMISSION-CONTEXT] Debug - Available permission data:', {
        sessionKeys: Object.keys(session),
        currentTenantKeys: Object.keys(currentTenant),
        currentTenantPermissions: session.currentTenantPermissions,
        effectivePermissions: session.effectivePermissions,
        crossTenantPermissions: session.crossTenantPermissions,
        currentTenantSimplePermissions: currentTenant.permissions,
        currentTenantPermissionCount: currentTenant.permissions?.length || 0
      });
      
      // More detailed debugging
      console.log(' [PERMISSION-CONTEXT] Detailed session data:', {
        sessionId: session.sessionId,
        hasCurrentTenantPermissions: !!session.currentTenantPermissions,
        hasEffectivePermissions: !!session.effectivePermissions,
        currentTenantPermissionsType: typeof session.currentTenantPermissions,
        effectivePermissionsType: typeof session.effectivePermissions,
        currentTenantPermissionsKeys: session.currentTenantPermissions ? Object.keys(session.currentTenantPermissions) : [],
        effectivePermissionsKeys: session.effectivePermissions ? Object.keys(session.effectivePermissions) : []
      });
      
      console.log(' [PERMISSION-CONTEXT] Detailed currentTenant data:', {
        tenantId: currentTenant.tenantId,
        tenantName: currentTenant.tenantName,
        hasPermissions: !!currentTenant.permissions,
        permissionsType: typeof currentTenant.permissions,
        permissionsLength: currentTenant.permissions?.length || 0,
        permissionsSample: currentTenant.permissions?.slice(0, 3) || []
      });
    }
  }, [session, currentTenant]);

  const permissionData = useMemo(() => {
    if (!session || !currentTenant) {
      return {
        currentTenantPermissions: null,
        crossTenantPermissions: {},
        effectivePermissions: {},
        permissionSummary: {
          resourcePermissionCounts: {},
          actionPermissionCounts: {},
          totalPermissions: 0
        },
        hasPermission: () => false,
        hasAnyPermission: () => false,
        hasAllPermissions: () => false,
        getTenantPermissions: () => null,
        getAccessibleTenants: () => [],
        isLoading
      };
    }

    // Extract permission data from session
    const currentTenantPermissions = session.currentTenantPermissions || null;
    const crossTenantPermissions = session.crossTenantPermissions || {};
    const effectivePermissions = session.effectivePermissions || {};
    const permissionSummary = session.permissionSummary || {
      resourcePermissionCounts: {},
      actionPermissionCounts: {},
      totalPermissions: 0
    };

    // Debug: Log what permission data we have
    console.log(' [PERMISSION-CONTEXT] Processing permission data:', {
      hasSessionPermissions: !!session.currentTenantPermissions,
      hasEffectivePermissions: !!session.effectivePermissions,
      hasCurrentTenantPermissions: !!currentTenant.permissions,
      currentTenantPermissionsKeys: session.currentTenantPermissions ? Object.keys(session.currentTenantPermissions) : [],
      effectivePermissionsKeys: session.effectivePermissions ? Object.keys(session.effectivePermissions) : [],
      currentTenantPermissionsArray: currentTenant.permissions || []
    });
    
    // Debug: Show the actual structure of permission data
    console.log(' [PERMISSION-CONTEXT] Session currentTenantPermissions structure:', session.currentTenantPermissions);
    console.log(' [PERMISSION-CONTEXT] Session effectivePermissions structure:', session.effectivePermissions);
    console.log(' [PERMISSION-CONTEXT] CurrentTenant permissions structure:', currentTenant.permissions);

    // For "can the user do X?" we use effectivePermissions only. The backend builds it as the
    // union of current-tenant + cross-tenant permissions, so it's the single source of truth.
    // currentTenantPermissions is still used for getTenantPermissions(tenantId) and for
    // hasPermission(resource, action, tenantId) when a specific tenant is passed.
    let finalEffectivePermissions: Record<string, PermissionSet> = effectivePermissions && Object.keys(effectivePermissions).length > 0
      ? effectivePermissions
      : {};

    if (Object.keys(finalEffectivePermissions).length === 0 && currentTenant.permissions && currentTenant.permissions.length > 0) {
      // Convert currentTenant.permissions array to new format
      console.log(' [PERMISSION-CONTEXT] Converting currentTenant.permissions to new format');
      console.log(' [PERMISSION-CONTEXT] Raw permissions array:', currentTenant.permissions);
      
      finalEffectivePermissions = {};
      currentTenant.permissions.forEach(permission => {
        const [resource, action] = permission.split(':');
        if (resource && action) {
          if (!finalEffectivePermissions[resource]) {
            finalEffectivePermissions[resource] = {} as PermissionSet;
          }
          (finalEffectivePermissions[resource] as any)[action] = true;
        }
      });
      
      console.log(' [PERMISSION-CONTEXT] Converted permissions:', finalEffectivePermissions);
    } else if (Object.keys(finalEffectivePermissions).length === 0) {
      const errorKey = `${session.sessionId}-${currentTenant.tenantId}`;
      if (errorLoggedRef.current !== errorKey) {
        logManager.log(' [PERMISSION-CONTEXT] No permission data available from any source');
        logManager.log(' [PERMISSION-CONTEXT] Session keys:', Object.keys(session));
        logManager.log(' [PERMISSION-CONTEXT] CurrentTenant keys:', Object.keys(currentTenant));
        logManager.log(' [PERMISSION-CONTEXT] CurrentTenant permissions:', currentTenant.permissions);
        logManager.log(' [PERMISSION-CONTEXT] Session currentTenantPermissions:', session.currentTenantPermissions);
        logManager.log(' [PERMISSION-CONTEXT] Session effectivePermissions:', session.effectivePermissions);
        errorLoggedRef.current = errorKey;
      }
      console.log(' [PERMISSION-CONTEXT] No permission data available - denying access');
    }

    const hasPermission = (resource: string, action: string, tenantId?: string): boolean => {
      // Debug logging for tenant permission checks
      /*if (resource === 'tenants' && action === 'read') {
        console.log(' [PERMISSION-CHECK] Checking tenants:read permission:', {
          resource,
          action,
          tenantId,
          currentTenantId: currentTenantPermissions?.tenantId,
          hasCurrentTenantPermissions: !!currentTenantPermissions,
          hasCrossTenantPermissions: Object.keys(crossTenantPermissions).length > 0,
          hasEffectivePermissions: Object.keys(finalEffectivePermissions).length > 0,
          currentTenantRoles: currentTenantPermissions?.roles?.map(r => ({
            name: r.roleName,
            isCrossTenant: r.isCrossTenant
          })) || []
        });
      }*/

      if (tenantId) {
        // Check specific tenant permissions
        if (tenantId === currentTenantPermissions?.tenantId) {
          const perms = currentTenantPermissions.permissions?.[resource];
          if (perms && action in perms) {
            const result = perms[action as keyof PermissionSet] || false;
            if (resource === 'tenants' && action === 'read') {
              console.log(' [] Current tenant permission result:', result);
            }
            return result;
          }
          return false;
        }
        const crossTenantPerms = crossTenantPermissions[tenantId];
        const perms = crossTenantPerms?.permissions?.[resource];
        if (perms && action in perms) {
          const result = perms[action as keyof PermissionSet] || false;
          if (resource === 'tenants' && action === 'read') {
            console.log(' [] Cross-tenant permission result:', result);
          }
          return result;
        }
        return false;
      }
      // Check effective permissions (across all accessible tenants)
      const perms = finalEffectivePermissions[resource];
      if (perms && action in perms) {
        const result = perms[action as keyof PermissionSet] || false;
        /*if (resource === 'tenants' && action === 'read') {
          console.log(' [PERMISSION-CHECK] Effective permission result:', result);
          console.log(' [] Effective permissions structure:', finalEffectivePermissions);
        }*/
        return result;
      }
      
      /*if (resource === 'tenants' && action === 'read') {
        console.log(' [PERMISSION-CHECK] No permission found, denying access');
      }*/
      return false;
    };

    const hasAnyPermission = (resource: string, actions: string[], tenantId?: string): boolean => {
      return actions.some(action => hasPermission(resource, action, tenantId));
    };

    const hasAllPermissions = (resource: string, actions: string[], tenantId?: string): boolean => {
      return actions.every(action => hasPermission(resource, action, tenantId));
    };

    const getTenantPermissions = (tenantId: string): PermissionData | null => {
      if (tenantId === currentTenantPermissions?.tenantId) {
        return currentTenantPermissions;
      }
      return crossTenantPermissions[tenantId] || null;
    };

    const getAccessibleTenants = (): string[] => {
      const tenants: string[] = [];
      if (currentTenantPermissions?.tenantId) {
        tenants.push(currentTenantPermissions.tenantId);
      }
      Object.keys(crossTenantPermissions).forEach(tenantId => {
        tenants.push(tenantId);
      });
      return tenants;
    };

    return {
      currentTenantPermissions,
      crossTenantPermissions,
      effectivePermissions: finalEffectivePermissions,
      permissionSummary,
      hasPermission,
      hasAnyPermission,
      hasAllPermissions,
      getTenantPermissions,
      getAccessibleTenants,
      isLoading
    };
  }, [session, currentTenant, isLoading]);

  return (
    <PermissionContext.Provider value={permissionData}>
      {children}
    </PermissionContext.Provider>
  );
};

export const usePermissions = (): PermissionContextType => {
  const context = useContext(PermissionContext);
  if (!context) {
    throw new Error('usePermissions must be used within a PermissionProvider');
  }
  return context;
}; 